import React, { useState, useRef, useEffect } from "react";
import {
  Facebook,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import emailjs from "@emailjs/browser";
import HeroSection from "../components/HeroSection";
import contactHero from "../assets/images/contact-hero.jpg";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const [errors, setErrors] = useState({});
  const [visibleElements, setVisibleElements] = useState(false);
  const contactRef = useRef(null);
  const formRef = useRef(null);

  // EmailJS Configuration - YOUR ACTUAL CREDENTIALS
  const EMAILJS_CONFIG = {
    SERVICE_ID: "service_gbvzeme",
    TEMPLATE_ID: "template_xc53xlq", 
    PUBLIC_KEY: "ooCABeQDAfIhtu9vB",
  };

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px",
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleElements(true);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    if (contactRef.current) {
      observer.observe(contactRef.current);
    }

    return () => observer.disconnect();
  }, [EMAILJS_CONFIG.PUBLIC_KEY]); // Fixed dependency

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || "Not provided",
          company: formData.company || "Not provided",
          subject: formData.subject || "General Inquiry",
          message: formData.message,
          to_email: "contact@madisonmw.com",
          timestamp: new Date().toLocaleString(),
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      if (result.status === 200) {
        setFormStatus({ submitting: false, submitted: true, error: null });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
        });
        setErrors({});

        // Hide success message after 5 seconds
        setTimeout(() => {
          setFormStatus({ submitting: false, submitted: false, error: null });
        }, 5000);
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setFormStatus({
        submitting: false,
        submitted: false,
        error: "Failed to send message. Please try again or contact us directly.",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const contactDetails = [
    {
      icon: <MapPin size={24} />,
      title: "Address",
      content: [
        "City Centre, ADL House",
        "P.O. Box 31507",
        "Lilongwe, Malawi"
      ],
      type: "text",
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      content: ["contact@madisonmw.com"],
      type: "email",
      link: "mailto:contact@madisonmw.com",
    },
    {
      icon: <Phone size={24} />,
      title: "Phone",
      content: ["01 775 433"],
      type: "phone",
      link: "tel:+2651775433",
    },
     {
      icon: <Phone size={24} />,
      title: "Phone",
      content: ["+265 998 955 221"],
      type: "phone",
      link: "tel:+265998955221",
    },
    {
      icon: <Clock size={24} />,
      title: "Business Hours",
      content: [
        "Monday - Friday: 8:00 AM - 5:00 PM",
        "Saturday & Sunday: Closed"
      ],
      type: "text",
    },
  ];

  const socialLinks = [
    { icon: <Facebook size={24} />, label: "Facebook", url: "https://www.facebook.com/p/Madison-Holdings-100046424636364/" },
    { icon: <Linkedin size={24} />, label: "LinkedIn", url: "https://www.linkedin.com/company/madison-holdings-limited/" },
  ];

  const subjectOptions = [
    "General Inquiry",
    "Business Partnership",
    "Service Request",
    "Support",
    "Other",
  ];

  return (
    <>
      <HeroSection
        title="CONTACT US"
        description="Get in touch with us to discuss how we can help your business thrive and achieve its goals"
        imageUrl={contactHero}
        overlay="dark"
        align="center"
      />

      <section className="contact-section" ref={contactRef}>
        <div className="contact-container">
          {/* Contact Information */}
          <div
            className={`contact-info ${visibleElements ? "slide-in-left" : ""}`}
            style={{
              opacity: visibleElements ? 1 : 0,
              transform: visibleElements ? "translateX(0)" : "translateX(-30px)",
              transition: "all 0.6s ease",
            }}
          >
            <h2>Get In Touch</h2>
            <p>
              We're here to help and answer any question you might have. We look
              forward to hearing from you and exploring how we can work together.
            </p>

            <div className="contact-details">
              {contactDetails.map((detail, index) => (
                <div
                  key={index}
                  className="contact-item"
                  style={{
                    opacity: visibleElements ? 1 : 0,
                    transform: visibleElements
                      ? "translateY(0)"
                      : "translateY(20px)",
                    transition: `all 0.6s ease ${index * 0.1 + 0.3}s`,
                  }}
                >
                  <div className="contact-item-icon">{detail.icon}</div>
                  <div className="contact-item-content">
                    <h4>{detail.title}</h4>
                    {detail.content.map((line, idx) => (
                      <p key={idx}>
                        {detail.link ? (
                          <a href={detail.link}>{line}</a>
                        ) : (
                          line
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-social">
              <h4>Follow Us</h4>
              <p>Stay connected with us on social media</p>
              <div className="social-links">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    aria-label={social.label}
                    title={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="map-placeholder">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3840.234567890123!2d33.7749!3d-13.9626!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1921f5f8d5c5c5c5%3A0x1234567890abcdef!2sADL%20House%2C%20Convention%20Drive%2C%20Lilongwe%2C%20Malawi!5e0!3m2!1sen!2smw!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: "12px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Madison Holdings Location - ADL House, Lilongwe"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className={`contact-form ${visibleElements ? "slide-in-right" : ""}`}
            style={{
              opacity: visibleElements ? 1 : 0,
              transform: visibleElements ? "translateX(0)" : "translateX(30px)",
              transition: "all 0.6s ease 0.2s",
            }}
          >
            {/* Success Message */}
            {formStatus.submitted && (
              <div className="form-success show">
                <CheckCircle size={20} />
                <span>
                  Thank you for your message! We'll get back to you within 24
                  hours.
                </span>
              </div>
            )}

            {/* Error Message */}
            {formStatus.error && (
              <div className="form-alert error">
                <AlertCircle size={20} />
                <span>{formStatus.error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} ref={formRef}>
              <div className="form-row">
                <div className={`form-group ${errors.name ? "error" : ""}`}>
                  <label>
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    disabled={formStatus.submitting}
                  />
                  {errors.name && (
                    <span className="form-error">{errors.name}</span>
                  )}
                </div>

                <div className={`form-group ${errors.email ? "error" : ""}`}>
                  <label>
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    disabled={formStatus.submitting}
                  />
                  {errors.email && (
                    <span className="form-error">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+265 123 4567"
                    disabled={formStatus.submitting}
                  />
                </div>

                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company Name"
                    disabled={formStatus.submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Subject</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={formStatus.submitting}
                >
                  <option value="">Select a subject</option>
                  {subjectOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className={`form-group ${errors.message ? "error" : ""}`}>
                <label>
                  Message <span className="required">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  placeholder="Tell us about your project or inquiry..."
                  disabled={formStatus.submitting}
                ></textarea>
                {errors.message && (
                  <span className="form-error">{errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={formStatus.submitting}
              >
                {formStatus.submitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}